import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { getBodyPartCoordinates } from "./BodyPartCoordinates";

function BodyPartSelector({
  selectedIndividual,
  bodyPartsData,
  setBodyPartsData,
  formData,
}) {
  const bodyParts = [
    "head",
    "neck",
    "shoulders",
    "leftArm",
    "rightArm",
    "leftLowerArm",
    "rightLowerArm",
    "leftHand",
    "rightHand",
    "upperBack",
    "lowerBack",
    "leftLeg",
    "rightLeg",
    "leftFoot",
    "rightFoot",
    "other",
  ];

  useEffect(() => {
    formData.bodyPartsData = bodyPartsData;
  }, [bodyPartsData]);

  const individualData = bodyPartsData.find(
    (data) => data.id === selectedIndividual,
  );
  const indiviDualName =
    selectedIndividual !== null
      ? formData.individualsList[selectedIndividual]
      : "";

  const handleCheckboxChange = (part) => {
    setBodyPartsData((prevData) =>
      prevData.map((data) =>
        data.id === selectedIndividual
          ? {
              ...data,
              bodyParts: {
                ...data.bodyParts,
                [part]: !data.bodyParts[part],
              },
            }
          : data,
      ),
    );
  };

  const handleConsciousChange = (value) => {
    setBodyPartsData((prevData) =>
      prevData.map((data) =>
        data.id === selectedIndividual
          ? { ...data, conscious: value === "yes" }
          : data,
      ),
    );
    formData.bodyPartsData = bodyPartsData;
  };

  const handleSeverityChange = (event) => {
    const severityLevel = event.target.value;
    setBodyPartsData((prevData) =>
      prevData.map((data) =>
        data.id === selectedIndividual
          ? { ...data, severity: severityLevel }
          : data,
      ),
    );
    formData.bodyPartsData = bodyPartsData;
  };

  if (!individualData) return null;

  return (
    <FormGroup>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
          {indiviDualName && (
            <>
              <strong>{indiviDualName}</strong>'s{" "}
            </>
          )}
          Body Parts Affected
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            height: "350px",
          }}
        >
          <svg
            viewBox="0 0 200 600"
            width="200"
            height="600"
            style={{ position: "relative" }}
          >
            {bodyParts.map((name, index) => {
              const { x, y, width, height, href, color } =
                getBodyPartCoordinates(name);
              const hueRotateDegree = 100 + index * 20;
              return (
                <g
                  key={name}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(name);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <image
                    href={href}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                      fill: color,
                      filter: individualData.bodyParts[name]
                        ? `hue-rotate(${hueRotateDegree}deg)`
                        : "none",
                      transition: "filter 0.2s ease",
                      opacity: "0.8",
                    }}
                  />
                </g>
              );
            })}
          </svg>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.1,
            width: "100%",
            maxWidth: "500px",
            justifyContent: "space-between",
          }}
        >
          {bodyParts.map((part) => (
            <FormControlLabel
              key={part}
              control={
                <Checkbox
                  checked={individualData.bodyParts[part] || false}
                  onChange={() => handleCheckboxChange(part)}
                />
              }
              label={part
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
              sx={{ width: "45%", whiteSpace: "nowrap" }}
            />
          ))}
        </Box>

        <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
          Responsive?
        </Typography>
        <RadioGroup
          row
          value={individualData.conscious ? "yes" : "no"}
          onChange={(e) => handleConsciousChange(e.target.value)}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>

        <FormControl fullWidth sx={{ marginTop: "10px" }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={individualData.severity || ""}
            onChange={handleSeverityChange}
            label="Severity"
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="moderate">Moderate</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="severe">Severe</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </FormGroup>
  );
}

export default BodyPartSelector;
